import { set as lodashSet, get as lodashGet } from "lodash";
import React, { useCallback } from "react";
import * as Yup from "yup";
import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import { postSocietyProfileWorkflowInstanceIdContract } from "shared/api/contracts/society/societyId/profiles/profileId/workflow/workflowId/instance/workflowInstanceId";
import { type SocietyWorkflowBodyInput } from "shared/api/types/society/[societyId]/workflow/[workflowId]/instance";
import PillarForm from "shared/components/pillar-form/PillarForm";
import { PillarFormDynamicInputComponent } from "shared/components/pillar-form/PillarFormDynamicInputComponentsMapper";
import PillarModalBody, {
  ModalMinWidth,
} from "shared/components/pillar-modal/NewPillarModalBody";
import { type PillarModalControl } from "shared/components/pillar-modal/usePillarModalControl";
import { useCustomSession } from "shared/context/CustomSessionContext";
import { type WorkflowInstanceView } from "shared/mappers/database/workflow/workflow-instance";
import { type WorkflowActionModal } from "shared/workflow/workflow-action";

const WorkflowActionModalComponent = ({
  workflowInstance,
  modalAction,
  control,
  refetchCallback,
  testid,
}: {
  workflowInstance: WorkflowInstanceView;
  modalAction: WorkflowActionModal;
  control: PillarModalControl;
  refetchCallback?: () => Promise<void>;
  testid: string;
}) => {
  const { society, profile } = useCustomSession();

  const formElements = modalAction.formElements ?? [];

  const hasSetStateField = formElements.some((el) => el.name === "setState");
  const validationSchema = Yup.object({
    ...(hasSetStateField
      ? { setState: Yup.string().required("Workflow state is required") }
      : {}),
  });

  const handleSubmit = useCallback(
    async (form: Record<string, unknown>) => {
      const body: SocietyWorkflowBodyInput = {
        eventName: modalAction.event,
        payloadValue: workflowInstance.workflowInstancePayload?.payloadValue,
        profileId: workflowInstance.profile?.profileId ?? 0,
        workflowInstancePayloadId: workflowInstance.workflowInstancePayloadId,
        formId: workflowInstance.workflowInstancePayload?.form?.formId,
      };

      formElements.forEach((element) => {
        const value = lodashGet(form, element.name);
        if (value !== undefined && value !== null && value !== "") {
          lodashSet(body, element.name, value);
        }
      });

      await apiRequestContractHandler(
        postSocietyProfileWorkflowInstanceIdContract,
        {
          params: {
            societyId: society!.societyId,
            profileId: profile!.profileId,
            workflowInstanceId: workflowInstance.workflowInstanceId ?? 0,
            workflowId: workflowInstance.workflow?.workflowId ?? 0,
          },
          body,
        },
      );

      if (refetchCallback) {
        await refetchCallback();
      }
      control.setIsOpen(false);
    },
    [
      workflowInstance,
      control,
      modalAction,
      formElements,
      society,
      profile,
      refetchCallback,
    ],
  );

  const dynamicInitialValues = formElements.reduce<Record<string, unknown>>(
    (acc, el) => {
      lodashSet(acc, el.name, "");
      return acc;
    },
    {},
  );

  return (
    <PillarModalBody
      control={control}
      title={modalAction.title}
      minW={ModalMinWidth.Sm_500}
      testid={`${testid}-workflow-action-modal-${modalAction.event}`}
    >
      <PillarForm
        overrideInitialValues={dynamicInitialValues}
        handleSubmit={handleSubmit}
        className="flex flex-col items-end"
        yupValidation={validationSchema}
      >
        {formElements.map((el, idx) => (
          <PillarFormDynamicInputComponent key={el.name ?? idx} element={el} />
        ))}

        <PillarForm.LoadingSubmitButton
          label={modalAction.confirmText ?? "Save"}
          className="mt-2 self-end"
          testid={`${testid}-workflow-action-modal-${modalAction.event}-submit`}
        />
      </PillarForm>
    </PillarModalBody>
  );
};

export default WorkflowActionModalComponent;
